import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import ReactECharts from "echarts-for-react";
import { Settings } from "lucide-react";
import * as Tooltip from "@radix-ui/react-tooltip";
import BaseTile from "../../../components/Tile/BaseTile";
import TileContent, { TileStatusContent } from "../../../components/Tile/TileContent";
import TileSettingsModal from "../../../components/common/TileSettingsModal";
import { useOverdueValueTimeline } from "./hooks/useOverdueValueTimeline";
import OverdueValueTimelineSettings from "./OverdueValueTimelineSettings";
import { OVERDUE_VALUE_TIMELINE_INFO_TEXT } from "./constants";

/** Small circle-i button; opens the project's white-card Radix tooltip on hover/focus. */
const InfoIcon = ({ label, description }) => (
  <Tooltip.Root>
    <Tooltip.Trigger asChild>
      <button
        type="button"
        className="inline-flex text-gray-400 hover:text-gray-500 transition-colors align-middle"
        aria-label={`${label} info`}
        onClick={(e) => e.stopPropagation()}
      >
        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
            clipRule="evenodd"
          />
        </svg>
      </button>
    </Tooltip.Trigger>
    <Tooltip.Portal>
      <Tooltip.Content
        side="top"
        align="center"
        sideOffset={6}
        className="max-w-[280px] bg-white border border-gray-200 rounded-md shadow-lg z-50"
      >
        <div className="px-2.5 py-1.5 border-b border-gray-100 bg-primary-50 rounded-t-md">
          <span className="text-xs font-medium text-primary-700">{label}</span>
        </div>
        <div className="px-2.5 py-1.5 text-xs leading-normal text-gray-500">
          {description}
        </div>
        <Tooltip.Arrow className="fill-white" />
      </Tooltip.Content>
    </Tooltip.Portal>
  </Tooltip.Root>
);

const GRANULARITIES = [
  { id: "day", label: "Tag" },
  { id: "week", label: "Woche" },
  { id: "month", label: "Monat" },
  { id: "year", label: "Jahr" },
];

const granBtnBase =
  "px-2 py-1 text-xs rounded-md border transition-colors whitespace-nowrap";
const granBtnActive = "bg-blue-600 text-white border-blue-600";
const granBtnInactive = "bg-white text-gray-700 border-gray-300 hover:bg-gray-50";

// ── Number formatting (German locale, matching existing components) ──────────
const fmtValue = (n) =>
  Number(n || 0).toLocaleString("de-DE", { maximumFractionDigits: 2 });

const fmtValueShort = (n) => {
  const num = Number(n || 0);
  if (Math.abs(num) >= 1_000_000)
    return `${(num / 1_000_000).toLocaleString("de-DE", { maximumFractionDigits: 1 })} M`;
  if (Math.abs(num) >= 1_000)
    return `${(num / 1_000).toLocaleString("de-DE", { maximumFractionDigits: 1 })} k`;
  return num.toLocaleString("de-DE", { maximumFractionDigits: 0 });
};

// ── X-axis label formatter (match AttributeSumLineChart style) ───────────────
const MONTH_NAMES_DE = [
  "Jan", "Feb", "Mär", "Apr", "Mai", "Jun",
  "Jul", "Aug", "Sep", "Okt", "Nov", "Dez",
];

function formatXLabel(isoDate, granularity) {
  if (!isoDate) return "";
  const d = new Date(isoDate + "T00:00:00Z");
  const day = d.getUTCDate();
  const month = MONTH_NAMES_DE[d.getUTCMonth()];
  const year = d.getUTCFullYear();

  if (granularity === "year") return String(year);
  if (granularity === "month") return `${month} ${year}`;
  if (granularity === "week") {
    // ISO week number
    const tmp = new Date(Date.UTC(year, d.getUTCMonth(), d.getUTCDate()));
    const dayOfWeek = tmp.getUTCDay() || 7;
    tmp.setUTCDate(tmp.getUTCDate() + 4 - dayOfWeek);
    const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
    const weekNo = Math.ceil(((tmp - yearStart) / 86400000 + 1) / 7);
    return `KW ${weekNo}`;
  }
  return `${day}. ${month}`;
}

const DEFAULT_TITLE = "Overdue Value Timeline";

function aggregateDailyData(dataPoints, granularity) {
  if (!dataPoints?.length) return [];
  if (granularity === "day") {
    return dataPoints.map((pt) => ({
      date: pt.date,
      avg_value: pt.total_value,
      peak_value: pt.total_value,
      peak_date: pt.date,
      delay_exposure: pt.total_value,
      day_count: 1,
      object_count: pt.object_count,
    }));
  }
  const buckets = new Map();
  for (const pt of dataPoints) {
    const d = new Date(pt.date + "T00:00:00Z");
    let key;
    if (granularity === "week") {
      const dow = d.getUTCDay() || 7;
      const mon = new Date(d);
      mon.setUTCDate(d.getUTCDate() - dow + 1);
      key = mon.toISOString().slice(0, 10);
    } else if (granularity === "month") {
      key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-01`;
    } else {
      key = `${d.getUTCFullYear()}-01-01`;
    }
    if (!buckets.has(key)) {
      buckets.set(key, { date: key, delay_exposure: 0, peak_value: 0, peak_date: key, object_count: 0, day_count: 0 });
    }
    const b = buckets.get(key);
    b.delay_exposure += pt.total_value;
    if (pt.total_value > b.peak_value) { b.peak_value = pt.total_value; b.peak_date = pt.date; }
    b.object_count = Math.max(b.object_count, pt.object_count);
    b.day_count += 1;
  }
  for (const b of buckets.values()) {
    b.avg_value = b.day_count > 0 ? b.delay_exposure / b.day_count : 0;
  }
  return Array.from(buckets.values()).sort((a, b) => a.date.localeCompare(b.date));
}

const OverdueValueTimeline = ({
  activeFile,
  onRefresh,
  onGetHeaderActions,
  onTitleChange,
  onRegisterSettingsGetter,
  initialSettings,
}) => {
  const { t } = useTranslation();
  const {
    componentTitle, setComponentTitle,
    objectType, setObjectType,
    targetDateAttribute, setTargetDateAttribute,
    completionSourceType, setCompletionSourceType,
    completionSourceValue, setCompletionSourceValue,
    valueAttribute, setValueAttribute,
    viewMode, setViewMode,
    granularity, setGranularity,
    dateRangeStart, setDateRangeStart,
    dateRangeEnd, setDateRangeEnd,
    fetchAttributes,
    allObjectAttributes,
    numericObjectAttributes,
    attrsLoading,
    chartData,
    loading,
    error,
    hasConfig,
    fetchData,
    datasetEnd,
  } = useOverdueValueTimeline(activeFile);

  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);

  // ── Restore initialSettings from parent (mount-only) ──────────────────────
  useEffect(() => {
    if (!initialSettings) return;
    if (initialSettings.componentTitle !== undefined) setComponentTitle(initialSettings.componentTitle);
    if (initialSettings.objectType !== undefined) setObjectType(initialSettings.objectType);
    if (initialSettings.targetDateAttribute !== undefined) setTargetDateAttribute(initialSettings.targetDateAttribute);
    if (initialSettings.completionSourceType !== undefined) setCompletionSourceType(initialSettings.completionSourceType);
    if (initialSettings.completionSourceValue !== undefined) {
      setCompletionSourceValue(
        Array.isArray(initialSettings.completionSourceValue)
          ? initialSettings.completionSourceValue
          : initialSettings.completionSourceValue ? [initialSettings.completionSourceValue] : []
      );
    }
    if (initialSettings.valueAttribute !== undefined) setValueAttribute(initialSettings.valueAttribute);
    if (initialSettings.viewMode !== undefined) setViewMode(initialSettings.viewMode);
    if (initialSettings.granularity !== undefined) setGranularity(initialSettings.granularity);
    if (initialSettings.dateRangeStart !== undefined) setDateRangeStart(initialSettings.dateRangeStart);
    if (initialSettings.dateRangeEnd !== undefined) setDateRangeEnd(initialSettings.dateRangeEnd);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Notify parent of title changes ────────────────────────────────────────
  useEffect(() => {
    onTitleChange?.(componentTitle || DEFAULT_TITLE);
  }, [componentTitle]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Register settings getter ──────────────────────────────────────────────
  useEffect(() => {
    onRegisterSettingsGetter?.(() => ({
      componentTitle,
      objectType,
      targetDateAttribute,
      completionSourceType,
      completionSourceValue,
      valueAttribute,
      viewMode,
      granularity,
      dateRangeStart,
      dateRangeEnd,
    }));
  }, [componentTitle, objectType, targetDateAttribute, completionSourceType, completionSourceValue,
      valueAttribute, viewMode, granularity, dateRangeStart, dateRangeEnd]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Register refresh callback ──────────────────────────────────────────────
  useEffect(() => {
    onRefresh?.(() => { if (hasConfig) fetchData(); });
  }, [onRefresh, hasConfig, fetchData]);

  // ── Initial fetch when config is complete, and re-fetch on any config change ──
  // `datasetEnd` is included so that a fast remount (config restored before the
  // new file's metadata arrives) re-fetches once the period_end clamp becomes available.
  useEffect(() => {
    if (hasConfig) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasConfig, objectType, targetDateAttribute, completionSourceType, completionSourceValue, valueAttribute, datasetEnd]);

  // ── Settings modal ─────────────────────────────────────────────────────────
  const handleOpenSettings = useCallback(() => setIsSettingsModalOpen(true), []);

  const headerActions = useMemo(
    () => (
      <button
        onClick={handleOpenSettings}
        className="flex items-center gap-1 px-2 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors"
      >
        <Settings className="w-3 h-3" />
        <span className="text-xs">{t('common:actions.settings')}</span>
      </button>
    ),
    [handleOpenSettings]
  );

  useEffect(() => {
    onGetHeaderActions?.(headerActions);
  }, [onGetHeaderActions, headerActions]);

  // ── Frontend date range filter (no backend call on Zeitraum changes) ─────────
  const filteredDataPoints = useMemo(() => {
    const points = chartData?.data_points ?? [];
    if (viewMode !== "daterange" || !dateRangeStart || !dateRangeEnd) return points;
    return points.filter((pt) => pt.date >= dateRangeStart && pt.date <= dateRangeEnd);
  }, [chartData, viewMode, dateRangeStart, dateRangeEnd]);

  // ── Frontend granularity aggregation (no backend call on granularity change) ──
  const aggregatedData = useMemo(
    () => aggregateDailyData(filteredDataPoints, granularity),
    [filteredDataPoints, granularity]
  );

  // ── ECharts option (improved styling) ─────────────────────────────────────
  const echartsOption = useMemo(() => {
    if (!aggregatedData?.length) return null;

    const dates     = aggregatedData.map((p) => p.date);
    const values    = aggregatedData.map((p) => p.avg_value);
    const counts    = aggregatedData.map((p) => p.object_count);
    const peaks     = aggregatedData.map((p) => p.peak_value);
    const exposures = aggregatedData.map((p) => p.delay_exposure);

    const yearBoundaries = [];
    const seenYears = new Set();
    for (const d of dates) {
      const year = new Date(d + "T00:00:00Z").getUTCFullYear();
      if (!seenYears.has(year)) {
        if (seenYears.size > 0) yearBoundaries.push({ date: d, year });
        seenYears.add(year);
      }
    }

    return {
      backgroundColor: "transparent",
      grid: {
        top: 16,
        right: 16,
        bottom: 52,
        left: 16,
        containLabel: true,
      },
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "cross", label: { backgroundColor: "#3B82F6" } },
        textStyle: { fontSize: 12 },
        formatter: (params) => {
          const idx = params[0]?.dataIndex ?? 0;
          const date = dates[idx] ?? "";
          const val = values[idx] ?? 0;
          const cnt = counts[idx] ?? 0;
          if (granularity === "day") {
            return [
              `<b>${formatXLabel(date, granularity)}</b>`,
              `Überfälliger Wert: <b>${fmtValue(val)} €</b>`,
              `Anzahl Objekte: <b>${cnt}</b>`,
            ].join("<br/>");
          }
          const peak = peaks[idx] ?? 0;
          const exposure = exposures[idx] ?? 0;
          return [
            `<b>${formatXLabel(date, granularity)}</b>`,
            `Ø Overdue Value: <b>${fmtValue(val)} €</b>`,
            `Peak Overdue Value: <b>${fmtValue(peak)} €</b>`,
            `Delay Exposure: <b>${fmtValue(exposure)} €-Tage</b>`,
            `Anzahl Objekte: <b>${cnt}</b>`,
          ].join("<br/>");
        },
      },
      xAxis: {
        type: "category",
        data: dates,
        boundaryGap: false,
        showMinLabel: true,
        axisLabel: {
          fontSize: 12,
          color: "#6b7280",
          hideOverlap: true,
          formatter: (val, index) => {
            const label = formatXLabel(val, granularity);
            if (index === 0 && (granularity === "day" || granularity === "week")) {
              return label + "\n" + new Date(val + "T00:00:00Z").getUTCFullYear();
            }
            return label;
          },
          rotate: granularity === "day" && dates.length > 30 ? 30 : 0,
        },
        axisLine: { lineStyle: { color: "#e5e7eb" } },
        axisTick: { lineStyle: { color: "#e5e7eb" } },
      },
      yAxis: {
        type: "value",
        axisLabel: {
          fontSize: 12,
          color: "#6b7280",
          formatter: (val) => fmtValueShort(val),
        },
        splitLine: { lineStyle: { color: "#f3f4f6", type: "dashed" } },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      series: [
        {
          name: valueAttribute || "Überfälliger Wert",
          type: "line",
          smooth: 0.3,
          symbol: "none",
          sampling: "average",
          lineStyle: { color: "#3B82F6", width: 2 },
          itemStyle: { color: "#3B82F6" },
          areaStyle: {
            color: {
              type: "linear",
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: "rgba(59, 130, 246, 0.35)" },
                { offset: 1, color: "rgba(59, 130, 246, 0.02)" },
              ],
            },
          },
          data: values,
          emphasis: {
            focus: "series",
            lineStyle: { width: 2.5 },
          },
          markLine: yearBoundaries.length > 0 ? {
            silent: true,
            symbol: ["none", "none"],
            lineStyle: { color: "#d1d5db", type: "solid", width: 1 },
            label: {
              show: true,
              position: "end",
              align: "left",
              distance: 4,
              formatter: (p) => p.name,
              color: "#9ca3af",
              fontSize: 10,
            },
            data: yearBoundaries.map(({ date, year }) => ({
              name: String(year),
              xAxis: date,
            })),
          } : undefined,
        },
      ],
      dataZoom: [
        {
          type: "inside",
          start: 0,
          end: 100,
          zoomOnMouseWheel: true,
          moveOnMouseMove: true,
        },
        {
          type: "slider",
          bottom: 4,
          height: 18,
          start: 0,
          end: 100,
          borderColor: "transparent",
          backgroundColor: "#f3f4f6",
          fillerColor: "rgba(59, 130, 246, 0.15)",
          handleStyle: { color: "#3B82F6", borderColor: "#3B82F6" },
          textStyle: { fontSize: 10, color: "#9ca3af" },
          showDetail: false,
        },
      ],
    };
  }, [aggregatedData, granularity, valueAttribute]);

  // ── Derived display state ──────────────────────────────────────────────────
  const hasData = !!(aggregatedData?.length > 0);
  const hasNonZeroData = hasData && aggregatedData.some((p) => p.avg_value > 0 || p.peak_value > 0);
  const displayTitle = componentTitle || DEFAULT_TITLE;

  // ── Summary stats (computed from aggregated data) ──────────────────────────
  const summaryStats = useMemo(() => {
    if (!aggregatedData?.length) return null;
    const peakBucket = aggregatedData.reduce(
      (best, p) => (p.peak_value > best.peak_value ? p : best),
      aggregatedData[0]
    );
    return {
      peakValue: peakBucket.peak_value,
      peakDate: peakBucket.peak_date,
      missingTargetDate: chartData?.objects_with_missing_target_date ?? 0,
    };
  }, [aggregatedData, chartData]);

  return (
    <>
      <BaseTile
        title={displayTitle}
        loading={loading}
        error={error}
        minHeight="lg"
        className="h-full"
        padding="sm"
      >
        <TileContent layout="flex" direction="column" gap="sm" grow className="-mt-2">
          {/* Granularity controls — always at top when config is set. The Zeitraum
              (custom date range) controls live in Settings instead. */}
          {hasConfig && (
            <Tooltip.Provider delayDuration={150}>
              <div className="flex items-center gap-4 px-1 py-1 text-xs">
                <div className="flex items-center gap-1">
                  {GRANULARITIES.map(({ id, label }) => (
                    <button
                      key={id}
                      type="button"
                      disabled={loading}
                      onClick={() => setGranularity(id)}
                      className={`${granBtnBase} ${granularity === id ? granBtnActive : granBtnInactive}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {summaryStats && (
                  <span className="text-gray-600 whitespace-nowrap">
                    Peak:{" "}
                    <strong className="text-gray-900">{fmtValue(summaryStats.peakValue)} €</strong>
                    {summaryStats.peakDate && (
                      <span className="text-gray-400 ml-1">
                        ({formatXLabel(summaryStats.peakDate, "day")})
                      </span>
                    )}
                  </span>
                )}
                <InfoIcon label={displayTitle} description={OVERDUE_VALUE_TIMELINE_INFO_TEXT} />
              </div>
            </Tooltip.Provider>
          )}

          {/* No config yet */}
          {!hasConfig && !loading && (
            <TileStatusContent
              status="info"
              title="Einstellungen konfigurieren"
              message="Bitte Object Type, Zieldatum, Abschlussdatum und Wertattribut in den Settings auswählen."
            />
          )}

          {/* Config set but no data returned */}
          {hasConfig && !loading && !error && chartData && !hasData && (
            <TileStatusContent
              status="empty"
              title="Keine Daten"
              message="Für die gewählte Konfiguration wurden keine Daten gefunden."
            />
          )}

          {/* Data with all-zero values */}
          {hasConfig && !loading && !error && hasData && !hasNonZeroData && (
            <TileStatusContent
              status="empty"
              title="Keine überfälligen Objekte"
              message="Im gewählten Zeitraum wurden keine überfälligen Objekte mit positivem Wert gefunden."
            />
          )}

          {/* Chart view */}
          {hasConfig && !loading && !error && hasData && hasNonZeroData && (
            <div className="flex flex-col gap-2 flex-1 min-h-0">
              {/* Missing target date warning */}
              {summaryStats?.missingTargetDate > 0 && (
                <div className="text-xs text-amber-600 px-1">
                  {summaryStats.missingTargetDate} ohne Zieldatum (ausgeschlossen)
                </div>
              )}

              {/* ECharts area chart */}
              <div className="flex-1 min-h-0">
                <ReactECharts
                  option={echartsOption}
                  style={{ height: "100%", width: "100%", minHeight: 200 }}
                  notMerge
                  lazyUpdate
                />
              </div>
            </div>
          )}
        </TileContent>
      </BaseTile>

      {/* Settings modal */}
      <TileSettingsModal
        open={isSettingsModalOpen}
        onOpenChange={setIsSettingsModalOpen}
        title="Overdue Value Timeline – Settings"
        getInitialValues={() => ({
          componentTitle,
          objectType,
          targetDateAttribute,
          completionSourceType,
          completionSourceValue,
          valueAttribute,
          viewMode,
          dateRangeStart,
          dateRangeEnd,
        })}
        onApply={(vals) => {
          setComponentTitle(vals.componentTitle);
          setObjectType(vals.objectType);
          setTargetDateAttribute(vals.targetDateAttribute);
          setCompletionSourceType(vals.completionSourceType);
          setCompletionSourceValue(vals.completionSourceValue);
          setValueAttribute(vals.valueAttribute);
          setViewMode(vals.viewMode);
          setDateRangeStart(vals.dateRangeStart);
          setDateRangeEnd(vals.dateRangeEnd);
        }}
        applyButtonText="Apply & Load"
        isApplyDisabled={(vals) =>
          !vals.objectType ||
          !vals.targetDateAttribute ||
          !vals.completionSourceType ||
          !vals.completionSourceValue?.length ||
          !vals.valueAttribute
        }
      >
        {({ values, setField }) => (
          <OverdueValueTimelineSettings
            activeFile={activeFile}
            componentTitle={values.componentTitle}
            onComponentTitleChange={(v) => setField("componentTitle", v)}
            objectType={values.objectType}
            onObjectTypeChange={(v) => {
              setField("objectType", v);
              setField("targetDateAttribute", "");
              setField("completionSourceValue", []);
              setField("valueAttribute", "");
              fetchAttributes(v);
            }}
            targetDateAttribute={values.targetDateAttribute}
            onTargetDateAttributeChange={(v) => setField("targetDateAttribute", v)}
            completionSourceType={values.completionSourceType}
            onCompletionSourceTypeChange={(v) => {
              setField("completionSourceType", v);
              setField("completionSourceValue", []);
            }}
            completionSourceValue={values.completionSourceValue}
            onCompletionSourceValueChange={(v) => setField("completionSourceValue", v)}
            valueAttribute={values.valueAttribute}
            onValueAttributeChange={(v) => setField("valueAttribute", v)}
            allObjectAttributes={allObjectAttributes}
            numericObjectAttributes={numericObjectAttributes}
            attrsLoading={attrsLoading}
            viewMode={values.viewMode}
            onViewModeChange={(v) => setField("viewMode", v)}
            dateRangeStart={values.dateRangeStart}
            onDateRangeStartChange={(v) => setField("dateRangeStart", v)}
            dateRangeEnd={values.dateRangeEnd}
            onDateRangeEndChange={(v) => setField("dateRangeEnd", v)}
          />
        )}
      </TileSettingsModal>
    </>
  );
};

export default OverdueValueTimeline;
